import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Tooltip,
} from 'components/mui';
import {
  SearchIcon, DeleteIcon, EditIcon, ContentCopyIcon, MoreVertIcon,
} from 'components/icons';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PublishIcon from '@mui/icons-material/Publish';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import axiosInstance from 'api/axiosInstance';
import { useFileBucketAccess } from './useFileBucketAccess';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mimeLabel(mime) {
  if (!mime) return 'Unknown';
  if (mime.startsWith('image/')) return 'Image';
  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('android') || mime.includes('apk')) return 'APK';
  if (mime.includes('zip') || mime.includes('compressed')) return 'Archive';
  if (mime.startsWith('video/')) return 'Video';
  if (mime.startsWith('audio/')) return 'Audio';
  return mime.split('/').pop()?.toUpperCase() || 'File';
}

/** Check if current client is SDS (Stream Data Systems) */
function isSdsClient() {
  const hostname = window.location.hostname.toLowerCase();
  // Alist is the only non-SDS client currently
  if (hostname.includes('alist')) return false;
  return true;
}

const EMPTY_PUBLISH_FORM = {
  download_key: '',
  display_name: '',
  description: '',
  version: '',
  icon_name: 'InsertDriveFile',
  icon_color: 'primary',
  tenant_code: '',
  sort_order: 0,
};

// ─── Component ──────────────────────────────────────────────────────────────

function FileBucketManager() {
  const { notifySuccess, notifyError } = useNotificationProvider();
  const { isAdmin, customerId } = useFileBucketAccess();

  // Tenant list (only for SDS users)
  const isSds = isSdsClient();
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Files table state
  const [files, setFiles] = useState([]);
  const [filesTotalCount, setFilesTotalCount] = useState(0);
  const [filesPage, setFilesPage] = useState(1);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesSearch, setFilesSearch] = useState('');
  const [hasMoreFiles, setHasMoreFiles] = useState(true);

  // 3-dot menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFile, setMenuFile] = useState(null);

  // Delete confirm dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });

  // Downloads state
  const [downloads, setDownloads] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);

  // Publish dialog
  const [publishDialog, setPublishDialog] = useState({ open: false, file: null });
  const [publishForm, setPublishForm] = useState(EMPTY_PUBLISH_FORM);

  // Edit download dialog
  const [editDialog, setEditDialog] = useState({ open: false, download: null });
  const [editForm, setEditForm] = useState(EMPTY_PUBLISH_FORM);

  // Delete download confirm dialog
  const [deleteDownloadDialog, setDeleteDownloadDialog] = useState({ open: false, download: null });

  // Delete loading states
  const [deletingFile, setDeletingFile] = useState(false);
  const [deletingDownload, setDeletingDownload] = useState(false);

  const FILES_PER_PAGE = 20;

  // ─── Data fetching ──────────────────────────────────────────────────────

  const fetchFiles = useCallback(async (page = 1, append = false) => {
    setFilesLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api3.2/bucket?page=${page}&limit=${FILES_PER_PAGE}`);
      const newFiles = data.files || [];
      setFiles((prev) => (append ? [...prev, ...newFiles] : newFiles));
      setFilesTotalCount(data.total || 0);
      setHasMoreFiles(newFiles.length === FILES_PER_PAGE);
      setFilesPage(page);
    } catch (error) {
      console.error('[FileBucketManager] fetchFiles:', error);
      notifyError('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  }, [notifyError]);

  const fetchDownloads = useCallback(async () => {
    setDownloadsLoading(true);
    try {
      const { data } = await axiosInstance.get('/api3.2/downloads');
      setDownloads(data || []);
    } catch (error) {
      console.error('[FileBucketManager] fetchDownloads:', error);
      notifyError('Failed to load downloads');
    } finally {
      setDownloadsLoading(false);
    }
  }, [notifyError]);

  useEffect(() => {
    fetchFiles(1);
    fetchDownloads();
  }, [fetchFiles, fetchDownloads]);

  // Fetch tenant list for SDS users
  useEffect(() => {
    if (!isSds) return;
    const fetchTenants = async () => {
      setTenantsLoading(true);
      try {
        const { data } = await axiosInstance.get('/api3.2/tenants');
        setTenants(data || []);
      } catch (error) {
        console.error('[FileBucketManager] fetchTenants:', error);
        // Endpoint may not exist yet — fail silently
      } finally {
        setTenantsLoading(false);
      }
    };
    fetchTenants();
  }, [isSds]);

  // ─── Upload handlers ────────────────────────────────────────────────────

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_public', isPublic ? 'true' : 'false');

      await axiosInstance.post('/api3.2/bucket/upload', formData);

      notifySuccess(`Uploaded ${file.name}`);
      fetchFiles(1);
    } catch (error) {
      console.error('[FileBucketManager] upload:', error);
      notifyError(error.response?.data?.error || error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  // ─── File actions ───────────────────────────────────────────────────────

  const handleCopyUrl = async (file) => {
    if (!file) return;
    const url = file.is_public
      ? `${window.location.origin}/api3.2/noauth/bucket/${file.id}`
      : `${window.location.origin}/api3.2/bucket/${file.id}`;
    try {
      await navigator.clipboard.writeText(url);
      notifySuccess('URL copied to clipboard');
    } catch {
      notifyError('Failed to copy URL to clipboard');
    }
    setMenuAnchor(null);
  };

  const handleDeleteFile = async () => {
    const file = deleteDialog.file;
    if (!file) return;
    setDeletingFile(true);
    try {
      await axiosInstance.delete(`/api3.2/bucket/${file.id}`);
      notifySuccess(`Deleted ${file.original_filename}`);
      setDeleteDialog({ open: false, file: null });
      fetchFiles(1);
    } catch (error) {
      console.error('[FileBucketManager] deleteFile:', error);
      notifyError('Failed to delete file');
    } finally {
      setDeletingFile(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMoreFiles && !filesLoading) {
      fetchFiles(filesPage + 1, true);
    }
  };

  // ─── Publish to downloads ───────────────────────────────────────────────

  const openPublishDialog = (file) => {
    if (!file) return;
    setPublishForm({
      ...EMPTY_PUBLISH_FORM,
      display_name: file.original_filename,
      download_key: file.original_filename
        .replace(/\.[^.]+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-'),
      // Default to user's own tenant for non-SDS, empty for SDS (admin chooses)
      tenant_code: isSds ? '' : (customerId || ''),
    });
    setPublishDialog({ open: true, file });
    setMenuAnchor(null);
  };

  const handlePublish = async () => {
    const file = publishDialog.file;
    if (!file) return;
    try {
      await axiosInstance.post('/api3.2/downloads', {
        ...publishForm,
        storage_path: file.storage_path,
        original_filename: file.original_filename,
        mime_type: file.mime_type,
        file_size_display: formatBytes(file.size_bytes),
        tenant_code: publishForm.tenant_code || null,
      });
      notifySuccess('Published to downloads');
      setPublishDialog({ open: false, file: null });
      fetchDownloads();
    } catch (error) {
      console.error('[FileBucketManager] publish:', error);
      const msg = error.response?.data?.error || 'Failed to publish download';
      notifyError(msg);
    }
  };

  // ─── Edit download ─────────────────────────────────────────────────────

  const openEditDialog = (dl) => {
    setEditForm({
      download_key: dl.download_key,
      display_name: dl.display_name,
      description: dl.description || '',
      version: dl.version || '',
      icon_name: dl.icon_name || 'InsertDriveFile',
      icon_color: dl.icon_color || 'primary',
      tenant_code: dl.tenant_code || (!isSds ? (customerId || '') : ''),
      sort_order: dl.sort_order ?? 0,
    });
    setEditDialog({ open: true, download: dl });
  };

  const handleEditSave = async () => {
    const dl = editDialog.download;
    if (!dl) return;
    try {
      await axiosInstance.put(`/api3.2/downloads/${dl.id}`, {
        ...editForm,
        storage_path: dl.storage_path,
        original_filename: dl.original_filename,
        mime_type: dl.mime_type,
        file_size_display: dl.file_size_display,
        tenant_code: editForm.tenant_code || null,
        is_active: dl.is_active,
      });
      notifySuccess('Download updated');
      setEditDialog({ open: false, download: null });
      fetchDownloads();
    } catch (error) {
      console.error('[FileBucketManager] editSave:', error);
      const msg = error.response?.data?.error || 'Failed to update download';
      notifyError(msg);
    }
  };

  // ─── Delete download ───────────────────────────────────────────────────

  const handleDeleteDownload = async () => {
    const dl = deleteDownloadDialog.download;
    if (!dl) return;
    setDeletingDownload(true);
    try {
      await axiosInstance.delete(`/api3.2/downloads/${dl.id}`);
      notifySuccess('Download removed');
      setDeleteDownloadDialog({ open: false, download: null });
      fetchDownloads();
    } catch (error) {
      console.error('[FileBucketManager] deleteDownload:', error);
      notifyError('Failed to remove download');
    } finally {
      setDeletingDownload(false);
    }
  };

  // ─── Filtered files ─────────────────────────────────────────────────────

  const filteredFiles = filesSearch
    ? files.filter((f) =>
        f.original_filename?.toLowerCase().includes(filesSearch.toLowerCase()),
      )
    : files;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 }, maxWidth: 1100 }}>

      {/* ── Title ────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h4">File Manager</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload, manage, and publish files from FileBucket storage.
        </Typography>
      </Paper>

      {/* ── Upload section ───────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Upload</Typography>

        <FormControlLabel
          control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
          label={isPublic ? 'Public (accessible without login)' : 'Private (login required)'}
          sx={{ mb: 2 }}
        />

        <Paper
          variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
            borderStyle: 'dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" gutterBottom>Drag and drop a file here</Typography>
              <Typography variant="body2" color="text.secondary">or click to browse</Typography>
            </>
          )}
        </Paper>
      </Paper>

      {/* ── Files table ──────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">
            Files {filesTotalCount > 0 && <Chip label={filesTotalCount} size="small" sx={{ ml: 1 }} />}
          </Typography>
          <TextField
            size="small"
            placeholder="Search files..."
            value={filesSearch}
            onChange={(e) => setFilesSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ width: { xs: '100%', sm: 250 } }}
          />
        </Box>

        {filesLoading && files.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} variant="rectangular" height={48} />)}
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {filesSearch ? 'No files match your search.' : 'No files uploaded yet.'}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Size</TableCell>
                    <TableCell>Visibility</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles.map((file) => (
                    <TableRow key={file.id} hover>
                      <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={file.original_filename}>
                          <span>{file.original_filename}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {mimeLabel(file.mime_type)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {formatBytes(file.size_bytes)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={file.is_public ? 'Public' : 'Private'}
                          size="small"
                          color={file.is_public ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {file.created_at ? new Date(file.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuFile(file); }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasMoreFiles && !filesSearch && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={filesLoading}
                >
                  {filesLoading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* ── Published Downloads ───────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Published Downloads</Typography>

        {downloadsLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(2)].map((_, i) => <Skeleton key={i} variant="rectangular" height={48} />)}
          </Box>
        ) : downloads.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No downloads published yet. Use the file actions menu to publish a file.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Key</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Version</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {downloads.map((dl) => (
                  <TableRow key={dl.id} hover>
                    <TableCell>{dl.display_name}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{dl.download_key}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{dl.version || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={dl.tenant_code || 'Global'}
                        size="small"
                        color={dl.tenant_code ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(dl)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDownloadDialog({ open: true, download: dl })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── 3-dot menu ───────────────────────────────────────────────── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleCopyUrl(menuFile)}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy URL
        </MenuItem>
        <MenuItem onClick={() => openPublishDialog(menuFile)}>
          <PublishIcon fontSize="small" sx={{ mr: 1 }} /> Publish to Downloads
        </MenuItem>
        <MenuItem
          onClick={() => { setDeleteDialog({ open: true, file: menuFile }); setMenuAnchor(null); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ── Delete file confirm dialog ───────────────────────────────── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, file: null })}>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.file?.original_filename}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialog({ open: false, file: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteFile} disabled={deletingFile}>
            {deletingFile ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Publish dialog ───────────────────────────────────────────── */}
      <Dialog
        open={publishDialog.open}
        onClose={() => setPublishDialog({ open: false, file: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish to Downloads</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Download Key (slug)"
            size="small"
            required
            value={publishForm.download_key}
            onChange={(e) => setPublishForm((p) => ({ ...p, download_key: e.target.value }))}
            helperText="URL-safe identifier, e.g. guard-android"
          />
          <TextField
            label="Display Name"
            size="small"
            required
            value={publishForm.display_name}
            onChange={(e) => setPublishForm((p) => ({ ...p, display_name: e.target.value }))}
          />
          <TextField
            label="Description"
            size="small"
            multiline
            rows={2}
            value={publishForm.description}
            onChange={(e) => setPublishForm((p) => ({ ...p, description: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Version"
              size="small"
              value={publishForm.version}
              onChange={(e) => setPublishForm((p) => ({ ...p, version: e.target.value }))}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Sort Order"
              size="small"
              type="number"
              value={publishForm.sort_order}
              onChange={(e) => setPublishForm((p) => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
              sx={{ width: 120 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Icon Name"
              size="small"
              value={publishForm.icon_name}
              onChange={(e) => setPublishForm((p) => ({ ...p, icon_name: e.target.value }))}
              helperText="MUI icon name"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Icon Color"
              size="small"
              value={publishForm.icon_color}
              onChange={(e) => setPublishForm((p) => ({ ...p, icon_color: e.target.value }))}
              helperText="primary, success, info, etc."
              sx={{ flex: 1 }}
            />
          </Box>
          {isSds ? (
            tenants.length > 0 ? (
              <TextField
                label="Tenant Scope"
                size="small"
                select
                value={publishForm.tenant_code}
                onChange={(e) => setPublishForm((p) => ({ ...p, tenant_code: e.target.value }))}
                helperText="Select a tenant, or leave empty for Global (all tenants)"
              >
                <MenuItem value="">
                  <em>Global (all tenants)</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.code || t.id} value={t.code || t.id}>
                    {t.name || t.code || t.id}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Tenant Code"
                size="small"
                value={publishForm.tenant_code}
                onChange={(e) => setPublishForm((p) => ({ ...p, tenant_code: e.target.value }))}
                helperText={tenantsLoading ? 'Loading tenants...' : 'Tenant code (leave empty for Global)'}
              />
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPublishDialog({ open: false, file: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={!publishForm.download_key || !publishForm.display_name}
            startIcon={<PublishIcon />}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit download dialog ─────────────────────────────────────── */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, download: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Download</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Download Key (slug)"
            size="small"
            required
            value={editForm.download_key}
            onChange={(e) => setEditForm((p) => ({ ...p, download_key: e.target.value }))}
          />
          <TextField
            label="Display Name"
            size="small"
            required
            value={editForm.display_name}
            onChange={(e) => setEditForm((p) => ({ ...p, display_name: e.target.value }))}
          />
          <TextField
            label="Description"
            size="small"
            multiline
            rows={2}
            value={editForm.description}
            onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Version"
              size="small"
              value={editForm.version}
              onChange={(e) => setEditForm((p) => ({ ...p, version: e.target.value }))}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Sort Order"
              size="small"
              type="number"
              value={editForm.sort_order}
              onChange={(e) => setEditForm((p) => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
              sx={{ width: 120 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Icon Name"
              size="small"
              value={editForm.icon_name}
              onChange={(e) => setEditForm((p) => ({ ...p, icon_name: e.target.value }))}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Icon Color"
              size="small"
              value={editForm.icon_color}
              onChange={(e) => setEditForm((p) => ({ ...p, icon_color: e.target.value }))}
              sx={{ flex: 1 }}
            />
          </Box>
          {isSds ? (
            tenants.length > 0 ? (
              <TextField
                label="Tenant Scope"
                size="small"
                select
                value={editForm.tenant_code}
                onChange={(e) => setEditForm((p) => ({ ...p, tenant_code: e.target.value }))}
                helperText="Select a tenant, or leave empty for Global (all tenants)"
              >
                <MenuItem value="">
                  <em>Global (all tenants)</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.code || t.id} value={t.code || t.id}>
                    {t.name || t.code || t.id}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Tenant Code"
                size="small"
                value={editForm.tenant_code}
                onChange={(e) => setEditForm((p) => ({ ...p, tenant_code: e.target.value }))}
                helperText={tenantsLoading ? 'Loading tenants...' : 'Tenant code (leave empty for Global)'}
              />
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditDialog({ open: false, download: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={!editForm.download_key || !editForm.display_name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete download confirm dialog ───────────────────────────── */}
      <Dialog
        open={deleteDownloadDialog.open}
        onClose={() => setDeleteDownloadDialog({ open: false, download: null })}
      >
        <DialogTitle>Remove Download</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteDownloadDialog.download?.display_name}</strong> from
            downloads? The underlying file will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDownloadDialog({ open: false, download: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteDownload} disabled={deletingDownload}>
            {deletingDownload ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FileBucketManager;
